/* ═══════════════════════════════════════════════════════════════
   FirmPrefs — firm-level UI DEFAULTS (firm_prefs table).

   The middle rung of the workspace-defaults ladder
   (docs/DESIGNER_UNIFIED_PLAN.md §2.9):

       the user's own saved layout  →  the FIRM default  →  out-of-box

   Every signed-in member READS these (the editor lays itself out from
   them). Writing one is an admin act — it changes what every colleague
   who never customised their workspace sees — and the server enforces
   that with admin_center.manage_firm_settings; this module never
   pretends otherwise.
   ═══════════════════════════════════════════════════════════════ */

import * as cache from './cloudCache';
import type { TableOpts } from './cloudCache';

const opts: TableOpts = { jsonFields: ['value'] };

interface FirmPrefRow {
  id: string;
  firmId: string;
  key: string;
  value: unknown;
  setBy?: string;
}

let firmId: string | null = null;
let map = new Map<string, unknown>();
let ready = false;
const subs = new Set<() => void>();

function emit() {
  subs.forEach((f) => f());
}

export const FirmPrefs = {
  isReady: (): boolean => ready,

  /** Load the firm's defaults. Call once after auth, beside Prefs.load. */
  load: async (fid: string): Promise<void> => {
    firmId = fid;
    try {
      const rows = await cache.list<FirmPrefRow>('firm_prefs', opts);
      map = new Map(rows.map((r) => [r.key, r.value]));
    } catch (e) {
      // A firm with no defaults yet, or a user whose role cannot read the
      // table, simply falls through to the out-of-box default. Never fatal.
      console.warn('[firm-prefs] load failed (using out-of-box defaults):', e);
    }
    ready = true;
    emit();
  },

  /** Sign-out / firm-switch. */
  reset: (): void => {
    firmId = null;
    map = new Map();
    ready = false;
  },

  get: (key: string): unknown => map.get(key),

  /** Re-pull from the server, bypassing the cache. */
  refetch: async (): Promise<void> => {
    if (!firmId) return;
    try {
      cache.invalidate('firm_prefs');
      const rows = await cache.list<FirmPrefRow>('firm_prefs', opts);
      map = new Map(rows.map((r) => [r.key, r.value]));
      emit();
    } catch (e) {
      console.warn('[firm-prefs] refetch failed:', e);
    }
  },

  /**
   * Publish a firm default. Rejects if the caller's role lacks
   * admin_center.manage_firm_settings — the server is the authority, so the
   * error is surfaced rather than swallowed and the caller can toast it.
   */
  set: async (key: string, value: unknown, setBy?: string): Promise<void> => {
    if (!firmId) throw new Error('Not signed in to a firm.');
    const safe = key.replace(/[^A-Za-z0-9_.-]/g, '_');
    await cache.save('firm_prefs', { id: `fp_${firmId}_${safe}`, firmId, key, value, setBy }, opts);
    map.set(key, value);
    emit();
  },

  subscribe: (fn: () => void): (() => void) => {
    subs.add(fn);
    return () => {
      subs.delete(fn);
    };
  },
};
